import { Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { FormModal } from '../../../components/FormModal'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import IFilter from '../../../interfaces/AceleraMagenta/index/IFilter'
import AceleraMagentaService from '../../../services/AceleraMagenta/AceleraMagentaService'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { UserService } from '../../../services/User/UserService'
import AceleraMagentaViewPage from './AceleraMagentaViewPage'

interface IAcelera {
  id: number
  employee_id: number
  created_by: string
  step: number
  done: boolean
  scoreMaturityDirection: number
  scoreMaturityOrientation: number
  scoreMaturitySupport: number
  scoreMaturityDelegation: number
  scoreEffectiveDelegation: number
  created_at: string
  updated_at: string
  link_pdf: string
  sector_id: number
  sector: ISector
  employee: {
    id: number
    name: string
    email: string
    birthdate: string
    nickname: string
    position: string
    created_at: string
    updated_at: string
  }
}

const INITIAL_FILTER: IFilter = {
  date: { start: '', end: '' },
  manager: '',
  name: '',
  sectorId: 0
}

export default function AceleraMagentaPage() {
  const modalState = useDisclosure()
  const aceleraMagentaService = new AceleraMagentaService()
  const [aceleraMagentaId, setAceleraMagentaId] = useState<number>()
  const [aceleras, setAceleras] = useState<IAcelera[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filter, setFilter] = useState<IFilter>(INITIAL_FILTER)
  const toast = useAppToast()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const isDirty =
    filter.name !== '' ||
    filter.manager !== '' ||
    filter.sectorId !== 0 ||
    filter.date.start !== '' ||
    filter.date.end !== ''

  const hasRole = useHasRole()
  const permissions = {
    downloadEvaluations: hasRole('download_evaluations'),
    deleteEvaluations: hasRole('delete_evaluations'),
    deleteSectorEvaluations: hasRole('delete_sector_evaluations')
  }
  const userService = new UserService()
  const [userSectorsIds, setUserSectorsIds] = useState<number[]>([])
  const sectorService = new SectorService()
  const [sectors, setSectors] = useState<ISector[]>([])

  const refreshTable = () => {
    setLoading(true)
    setLoadError(false)
    aceleraMagentaService
      .list(filter)
      .then((response) => {
        setAceleras(response.data)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as avaliações', error)
      })
      .finally(() => setLoading(false))
  }

  const fetchUserSectors = () => {
    userService.getSectors().then((response) => {
      setUserSectorsIds(response.map((sectors) => sectors.id))
    })
  }

  const confirmDelete = (id: number) => setDeletingId(id)

  const handleDelete = async () => {
    if (deletingId === null) {
      return
    }

    setIsDeleting(true)
    try {
      await aceleraMagentaService.delete(deletingId)
      toast.success('Registro excluído')
      setDeletingId(null)
      refreshTable()
    } catch (error) {
      toast.error('Erro ao excluir o registro', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const fetchSectors = () => {
    sectorService.listForSelect().then((response) => {
      setSectors(response)
    })
  }

  useEffect(() => {
    fetchUserSectors()
    fetchSectors()
  }, [])

  // Filtra ao vivo, como as demais avaliações — o botão "Filtrar" existia porque um campo de
  // texto sem debounce dispararia uma requisição por tecla, o que o `FilterSearch` resolve.
  useEffect(() => {
    refreshTable()
  }, [filter])

  return (
    <>
      <PageTitle title="Acelera Magenta" description="Avaliações de maturidade e delegação dos colaboradores" />

      <ActionFab to="/acelera-magenta/new" label="Nova avaliação Acelera Magenta" openInNewTab />

      <FilterBar onClear={() => setFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSearch
          label="Nome"
          value={filter.name}
          onChange={(name) => setFilter({ ...filter, name })}
          placeholder="Buscar por nome"
        />
        <FilterSelect
          label="Setor"
          value={filter.sectorId}
          onChange={(sectorId) => setFilter({ ...filter, sectorId: Number(sectorId) })}
          options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
          // A listagem trata `0` como "sem filtro de setor", não `''`.
          emptyValue={0}
        />
        <FilterSearch
          label="Gestor"
          value={filter.manager}
          onChange={(manager) => setFilter({ ...filter, manager })}
          placeholder="Buscar por gestor"
        />
        <FilterDateRange label="Data" value={filter.date} onChange={(date) => setFilter({ ...filter, date })} />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Colaborador</Th>
              <Th>Realizado por</Th>
              <Th>Realizado em</Th>
              <Th>Setor</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : loadError ? (
              <TableErrorRow colSpan={6} entity="avaliações" feminine onRetry={refreshTable} />
            ) : aceleras.length === 0 ? (
              <TableEmptyRow colSpan={6} entity="avaliação" feminine />
            ) : (
              aceleras.map((acelera: IAcelera) => (
                <Tr key={acelera.id}>
                  <Td>{acelera.id}</Td>
                  <Td>{acelera.employee.name}</Td>
                  <Td>{acelera.created_by}</Td>
                  <Td>{formatDateTime(acelera.created_at, { showSeconds: true })}</Td>
                  <Td>
                    <EmptyCell value={acelera.sector?.name} />
                  </Td>
                  <Td>
                    {!!acelera.done && (
                      <>
                        <ActionButton
                          action="view"
                          onClick={() => {
                            modalState.onOpen()
                            setAceleraMagentaId(acelera.id)
                          }}
                        />
                        {permissions.downloadEvaluations && (
                          <a href={acelera.link_pdf} download="Acelera Magenta.pdf" target="_blank" rel="noreferrer">
                            <ActionButton action="download" />
                          </a>
                        )}
                      </>
                    )}
                    {!acelera.done && <ActionButton action="edit" to={`/acelera-magenta/${acelera.id}`} />}
                    {(permissions.deleteEvaluations ||
                      (userSectorsIds.includes(acelera.sector_id) && permissions.deleteSectorEvaluations)) && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          confirmDelete(acelera.id)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <FormModal isOpen={modalState.isOpen} onClose={modalState.onClose} size="wide">
        {aceleraMagentaId != undefined && (
          <AceleraMagentaViewPage aceleraMagentaId={aceleraMagentaId} skipLastStep={false} />
        )}
      </FormModal>
      <ConfirmDialog
        isOpen={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        action="delete"
        entity="acelera magenta"
        isLoading={isDeleting}
      />
    </>
  )
}
